import { Redirect } from 'next'
import * as R from 'ramda'

import { compareVersion as compareVersionNew, extractVersion, NAVER_APP_REGEX } from '@local/utils'

import { Nullable } from '@web/types'
import { ExcludeMyPlaceAPIInputEnv, MyPlaceAPIEnv } from '@web/types/napp'

import { IS_BROWSER, MAPS_REFERRER_KEY, PHASE } from './constants'

export const isFromApp: (userAgent: string) => boolean = R.test(/NAVER\((.*?)\)/)

// e.g. "... NAVER(inapp; map; 101; 5.29.1.1)"
const MAPS_USER_AGENT = 'NAVER(inapp; map;'
export const isFromMapsApp = (userAgent: string) => userAgent.includes(MAPS_USER_AGENT)

// e.g. "... NAVER(inapp; search; 2000; 12.8.1)"
const NAVER_APP_USER_AGENT = 'NAVER(inapp; search;'
export const isFromNaverApp = (userAgent: string) => userAgent.includes(NAVER_APP_USER_AGENT)

export interface MapAppVersion {
  major: number
  minor: number
  patch: number
}

export const mapsVersionsNew = {
  // 지도앱에서 호텔 리뷰 작성 sdk 호출 가능한 버전
  HOTEL_REVIEW_SDK: '5.31.0',
  // 지도앱 my탭에서 sdk 호출 가능한 버전
  MY_TAB_SDK: '5.22.1',
  // 지도앱 my탭/인앱브라우저에서 sdk 호출 가능한 버전
  IN_APP_BROWSER_SDK: '5.23.0',
  // 지도앱에서 인앱 오픈시 뷰 변경이 가능한 버전
  TAB_STATE_SDK: '5.25.0',
  // 지도앱에서 즐겨찾기로 랜딩이 가능한 버전
  FAVORITE_LANDING_SDK: '5.31.0',
  // 지도앱에서 Place Id 없이 Place 마커로 랜딩 가능한 버전
  ADDRESS_MARK_LANDING_SDK: '5.32.0',
  // 지도앱에서 운전분석으로 랜딩이 가능한 버전
  MY_DRIVING_SDK: '5.30.0',
}

export const naverAppVersionsNew = {
  // 네앱에서 호텔 리뷰 작성 sdk 호출 가능한 버전
  HOTEL_REVIEW_SDK: '12.9.0',
}

export const isSupportVersion = (targetVersion: string) =>
  R.pipe(extractVersion(NAVER_APP_REGEX), compareVersionNew(targetVersion, R.gte))

export const shouldOpenHotelSDK: (userAgent: string) => boolean = R.pipe(
  R.ifElse(
    isFromApp,
    R.ifElse(
      isFromMapsApp,
      isSupportVersion(mapsVersionsNew.HOTEL_REVIEW_SDK),
      isSupportVersion(naverAppVersionsNew.HOTEL_REVIEW_SDK),
    ),
    R.always(false),
  ),
)

export const shouldOpenSaveMapSDK: (userAgent: string) => boolean = R.pipe(
  R.ifElse(isFromMapsApp, isSupportVersion(mapsVersionsNew.TAB_STATE_SDK), R.always(false)),
)

export const createRedirect = (destination: string): { redirect: Redirect } => ({
  redirect: { permanent: false, destination },
})

export const getMapAppVersion = (userAgent?: string): Nullable<MapAppVersion> => {
  if (!userAgent) return null

  if (userAgent.indexOf(MAPS_USER_AGENT) < 0) return null

  const splitted = userAgent.split(';')
  if (!splitted.length) return null

  const version = splitted[splitted.length - 1].split('.')
  if (!version.length) return null

  const major = parseInt(version[0], 10)
  const minor = parseInt(version[1], 10)
  const patch = parseInt(version[2], 10)

  if (isNaN(major) || isNaN(minor) || isNaN(patch)) return null

  return {
    major,
    minor,
    patch,
  }
}

export const getNaverAppVersion = (userAgent?: string): Nullable<MapAppVersion> => {
  if (!userAgent) return null

  if (userAgent.indexOf(NAVER_APP_USER_AGENT) < 0) return null

  const splitted = userAgent.split(';')
  if (!splitted.length) return null

  const version = splitted[splitted.length - 1].split('.')
  if (!version.length) return null

  const major = parseInt(version[0], 10)
  const minor = parseInt(version[1], 10)
  const patch = parseInt(version[2], 10)

  if (isNaN(major) || isNaN(minor) || isNaN(patch)) return null

  return {
    major,
    minor,
    patch,
  }
}

enum VersionUnit {
  MAJOR = 'major',
  MINOR = 'minor',
  PATCH = 'patch',
}

export enum Compare {
  SMALLER = -1,
  EQUAL = 0,
  BIGGER = 1,
}

export const compareVersion = (sourceVersion: MapAppVersion, targetVersion: MapAppVersion): Compare => {
  const versionUnits = [VersionUnit.MAJOR, VersionUnit.MINOR, VersionUnit.PATCH]

  let result = Compare.EQUAL

  for (let i = 0; i < versionUnits.length; i += 1) {
    const baseVersionUnit = sourceVersion[versionUnits[i]]
    const clientVersionUnit = targetVersion[versionUnits[i]]

    if (baseVersionUnit > clientVersionUnit) {
      result = Compare.SMALLER
      break
    }

    if (baseVersionUnit < clientVersionUnit) {
      result = Compare.BIGGER
      break
    }
  }

  return result
}

export const mapsVersions = {
  HOTEL_REVIEW_SDK: {
    // 지도앱에서 호텔 리뷰 작성 sdk 호출 가능한 버전
    major: 5,
    minor: 31,
    patch: 0,
  },
  MY_TAB_SDK: {
    // 지도앱 my탭에서 sdk 호출 가능한 버전
    major: 5,
    minor: 22,
    patch: 1,
  },
  IN_APP_BROWSER_SDK: {
    // 지도앱 my탭/인앱브라우저에서 sdk 호출 가능한 버전
    major: 5,
    minor: 23,
    patch: 0,
  },
  TAB_STATE_SDK: {
    // 지도앱에서 인앱 오픈시 뷰 변경이 가능한 버전
    major: 5,
    minor: 25,
    patch: 0,
  },
  FAVORITE_LANDING_SDK: {
    // 지도앱에서 즐겨찾기로 랜딩이 가능한 버전
    major: 5,
    minor: 31,
    patch: 0,
  },
  ADDRESS_MARK_LANDING_SDK: {
    // 지도앱에서 Place Id 없이 Place 마커로 랜딩 가능한 버전
    major: 5,
    minor: 32,
    patch: 0,
  },
  MY_TAB_NATIVE_SDK: {
    // MY탭이 네이티브로 전환한 버전
    major: 5,
    minor: 32,
    patch: 0,
  },
  MAPS_PLACE_DETAIL: {
    // 지도앱에서 장소상세로 랜딩이 가능한 버전
    major: 5,
    minor: 33,
    patch: 0,
  },
  MY_DRIVING_SDK: {
    // 지도앱에서 운전분석으로 랜딩이 가능한 버전
    major: 5,
    minor: 30,
    patch: 0,
  },
  MAPS_REVIEW_EDIT_BRIDGE: {
    // 지도앱에서 리뷰 수정 브릿지로 랜딩이 가능한 버전
    major: 5,
    minor: 35,
    patch: 0,
  },
}

export const naverAppVersions = {
  NAVERAPP_REVIEW_EDIT_BRIDGE: {
    // 리뷰 수정 브릿지로 랜딩이 가능한 네앱 ios 버전
    major: 12,
    minor: 12,
    patch: 0,
  },
}

export const setMapsReferrer = (referrer: string) => {
  return window.sessionStorage.setItem(MAPS_REFERRER_KEY, referrer)
}

export const getMapsReferrer = () => {
  return window.sessionStorage.getItem(MAPS_REFERRER_KEY)
}

export const checkIsMapsAppView = ({ mapAppVersion }: { mapAppVersion: MapAppVersion | null }) => {
  if (mapAppVersion) {
    if (compareVersion(mapsVersions.IN_APP_BROWSER_SDK, mapAppVersion) !== Compare.SMALLER) {
      return true
    }

    if (getMapsReferrer() != null) {
      return compareVersion(mapsVersions.MY_TAB_SDK, mapAppVersion) !== Compare.SMALLER
    }
  }

  return false
}

export const checkIsNaverAppView = (userAgent: string) => !!(isFromNaverApp(userAgent) && window.MyPlaceAPI)

const getNaverAppSDKEnv = (): MyPlaceAPIEnv => {
  if (PHASE.includes('beta')) {
    return 'beta'
  }

  switch (PHASE) {
    case 'qa':
      return 'qa'
    case 'stage':
      return 'stage'
    case 'production':
      return 'real'
    default:
      return 'qa'
  }
}

export const getNaverAppSDKInputString = (input: ExcludeMyPlaceAPIInputEnv) =>
  JSON.stringify({ ...input, env: getNaverAppSDKEnv() })

export const goToNaverAppSDK = (input: ExcludeMyPlaceAPIInputEnv) =>
  window.MyPlaceAPI.launch(getNaverAppSDKInputString(input))

/**
 * @param napp url query string "napp"
 */
export const isFromOpenMain = (napp?: string) => (napp ? napp.toString().indexOf('mysection') > -1 : false)

const NA_RECEIPT_HASH = '#uploadReceipt'

/**
 * @param hash url hash, ex. some/path/page#something => "something"
 */
export const isFromNa = (hash?: string) => hash === NA_RECEIPT_HASH

export const getBrowserReferrer = () => {
  if (!IS_BROWSER) return undefined
  return window.document.referrer
}

export const getBrowserUserAgent = () => {
  if (!IS_BROWSER) return undefined
  return window.navigator.userAgent
}

export const checkHasNaverClipEditor = (userAgent: string) =>
  !!(isFromNaverApp(userAgent) && window.naverappapi?.openClipEditor)
export const checkHasNaverClipViewer = (userAgent: string) => !!(isFromNaverApp(userAgent) && window.inapphome)
export const goToNaverClipEditor = (input: string) => window.naverappapi.openClipEditor(input)
export const goToNaverClipViewer = (input: string) => window.inapphome.playShortFormV2(input)
